import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { UserButton } from '@clerk/react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BusinessSwitcher } from '@/components/BusinessSwitcher';

function BackArrowIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M12.7 15.8a1 1 0 0 1-1.42 0l-5-5a1 1 0 0 1 0-1.42l5-5a1 1 0 1 1 1.42 1.42L8.42 10l4.28 4.3a1 1 0 0 1 0 1.5Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Shared layout for the card pages (Income, Expense, …). Mirrors the dashboard
 * header — business switcher, theme toggle, user button — but for a sub-page:
 * a back-to-dashboard control and the page title in place of the greeting.
 */
export function AppShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-8 flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/dashboard"
            aria-label="Back to dashboard"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <BackArrowIcon />
          </Link>
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-bold text-slate-900 dark:text-slate-100">
              {title}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">WanEntry</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <BusinessSwitcher />
          <ThemeToggle />
          <UserButton
            afterSignOutUrl="/"
            appearance={{ elements: { avatarBox: 'h-9 w-9' } }}
          />
        </div>
      </header>

      <main>{children}</main>

      <footer className="mt-12 text-center text-xs text-slate-500 dark:text-slate-500">
        WanEntry v0.1.0 — Pre-alpha
      </footer>
    </div>
  );
}
